<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's'
      }
    ]"
    v-test="'page-signup'"
  >
    <Flex
      v-test="'signup-form'"
      :direction="'column'"
      :gap="2"
    >
      <Flex :direction="'column'">
        <BaseHeading
          v-if="!config.isTreatwellDomain"
          :size="$screen === 's' ? 'l' : 'xl'"
          center
          v-test="'signup-heading'"
        >
          {{ $t('signup.heading', brandName()) }}
        </BaseHeading>
        <img
          v-else
          src="/img/signup/te_full_pink.svg"
          alt="Treatwell Expert"
          :class="$style.treatwellLogo"
        >
        <BaseText center>
          {{ $t('signup.description') }}
        </BaseText>
      </Flex>
      <BaseForm @submit="signup">
        <Flex :direction="'column'">
          <BaseInput
            v-model="name"
            :label="$t('signup.name')"
            name="name"
            required
            :minLength="2"
            v-test="'signup-name'"
          />
          <BaseInput
            v-model="companyName"
            :label="$t('signup.company_name')"
            required
            :minLength="2"
            :error="companyNameErrorString"
            v-test="'signup-company'"
          />
          <BaseInput
            v-model="email"
            :label="$t('signup.email')"
            type="email"
            name="email"
            required
            :error="emailErrorString"
            v-test="'signup-email'"
          />
          <BaseInput
            v-if="showPhone"
            v-model="phone"
            :label="$t('signup.phone')"
            type="tel"
            :regex="COMPANY_PHONE_VALIDATION_REGEX"
            :defaultCountry="countryCode"
            name="phone"
            :required="isPhoneRequired"
            v-test="'signup-phone'"
          />
          <BaseInput
            v-model="password"
            :label="$t('signup.password')"
            type="password"
            name="password"
            required
            newPassword
            v-test="'signup-password'"
          />

          <BaseText center>
            <span v-html="termsConditionsText" />
          </BaseText>
          <div
            v-if="isTurnstileLoading || turnstileError"
            :class="$style.turnstilePlaceholder"
          >
            <BaseText
              v-if="isTurnstileLoading"
              size="s"
              center
            >
              {{ $t('signup.turnstile.loading') }}
            </BaseText>
            <div
              v-else-if="turnstileError"
              :class="$style.turnstileError"
            >
              <BaseText size="s">
                {{ $t('signup.turnstile.error') }}
              </BaseText>
              <BaseText
                size="s"
                link
                @click="retryTurnstile"
              >
                {{ $t('signup.turnstile.retry') }}
              </BaseText>
            </div>
          </div>
          <Turnstile
            v-show="!isTurnstileLoading && !turnstileError"
            action="signup"
          />
          <BaseButton
            :disabled="isTurnstileLoading || !turnstileCompleted"
            :loading="isLoading"
            size="l"
            fullWidth
            submitForm
            v-test="'signup-submit'"
          >
            {{ $t('signup.button') }}
          </BaseButton>
        </Flex>
      </BaseForm>
    </Flex>
    <div :class="$style.nav">
      <BaseText inline>{{ $t('signup.login_1') }}&nbsp; </BaseText>
      <BaseText
        :routerLink="{ name: 'login' }"
        bold
        v-test="'btn-login'"
      >
        {{ $t('signup.login_2') }}
      </BaseText>
    </div>
  </div>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { defineComponent } from 'vue';
import $axios from '@/axios';
import { useTurnstile } from '@/components/turnstile/hook';
import Turnstile from '@/components/turnstile/index.vue';
import config from '@/config';
import { brandName } from '@/helpers/brand-name';
import { logValidationError } from '@/helpers/datadog';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import {
  NativeAppMessage,
  sendNativeAppMessage,
} from '@/helpers/native-app-bridge';
import { COMPANY_PHONE_VALIDATION_REGEX } from '@/helpers/regex';
import { modal } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { usePageLayoutStore } from '@/stores/page-layout';
import { useSessionStore } from '@/stores/session';

export default defineComponent({
  components: {
    Turnstile,
  },
  setup() {
    const utmAttributes = inject('utmAttributes');
    const campaignAttributes = inject('campaignAttributes');
    const salesforceAttributes = inject('salesforceAttributes');

    const {
      turnstileCompleted,
      turnstileToken,
      isTurnstileLoading,
      turnstileError,
      resetTurnstile,
      retryTurnstile,
    } = useTurnstile();

    return {
      utmAttributes,
      campaignAttributes,
      salesforceAttributes,
      turnstileCompleted,
      turnstileToken,
      isTurnstileLoading,
      turnstileError,
      resetTurnstile,
      retryTurnstile,
      config,
      brandName,
    };
  },
  data() {
    return {
      name: '',
      companyName: '',
      email: '',
      password: '',
      newsletter: false,
      isLoading: false,
      emailErrorString: null,
      companyNameErrorString: null,
      phoneNumberValid: false,
      countryCode: '',
      phone: '',
      COMPANY_PHONE_VALIDATION_REGEX,
    };
  },
  watch: {
    email() {
      this.emailErrorString = null;
    },
    companyName() {
      this.companyNameErrorString = null;
    },
  },
  computed: {
    termsConditionsText() {
      return this.$t('signup.terms_conditions_1', {
        terms: `<a href="${this.$t(
          'signup.terms_conditions_url',
        )}" target="_blank">${this.$t('signup.terms_conditions_2')}</a>`,
      });
    },
    referrerOptions() {
      const choices = [
        'search_engine',
        'social_media',
        'trade_show',
        'online_ad',
        'review_site',
        'online_community',
        'sales_rep',
        'word_of_mouth',
        'other',
      ];

      return choices.map((choice) => ({
        label: this.$t(`signup.referrer_options.${choice}`),
        value: choice,
      }));
    },
    isUkVisitor() {
      return ['gb', 'je', 'gg', 'im'].includes(this.countryCode);
    },
    isPhoneRequired() {
      return this.isUkVisitor || this.countryCode === 'lv';
    },
    showPhone() {
      return ['nl', 'lu', 'be', 'de', 'lv', 'gb', 'je', 'gg', 'im'].includes(
        this.countryCode,
      );
    },
  },
  methods: {
    parseEmailError(error: string) {
      if (error.includes('is not available')) {
        this.emailErrorString = this.$t('signup.errors.email_in_use');
      } else {
        this.emailErrorString = this.$t('global.validations.invalid', {
          value: this.$t('global.email_address'),
        });
      }
    },

    signup() {
      if (!this.turnstileCompleted) {
        return;
      }

      this.isLoading = true;

      const signupAttributes = {
        companyName: this.companyName,
        email: this.email,
        fullName: this.name,
        password: this.password,
        turnstileToken: this.turnstileToken,
        updateNotifications: this.newsletter,
        locale: this.$route.params.locale,
        treatwellBrand: config.isTreatwellDomain,
        ...(this.phone.length > 0
          ? { phone: this.phone.replaceAll(' ', '') }
          : null),
      };

      this.$apollo
        .mutate({
          mutation: gql`
            mutation signup($input: SignupInput!) {
              signup(input: $input) {
                company {
                  id
                }
                error
              }
            }
          `,
          variables: {
            input: {
              signupAttributes,
              utmAttributes: this.utmAttributes,
              campaignAttributes: this.campaignAttributes,
              salesforceAttributes: this.salesforceAttributes,
            },
          },
        })
        .then(({ data: { signup } }) => {
          if (signup.error?.email) {
            this.parseEmailError(signup.error.email[0]);
            this.isLoading = false;
            this.resetTurnstile();
            return;
          }

          if (signup.error?.['company.name']) {
            this.companyNameErrorString = this.$t(
              'company.errors.invalid_company_name',
            );
            this.isLoading = false;
            this.resetTurnstile();
            return;
          }

          if (
            signup.error?.code === 403 &&
            signup.error?.message === 'signup_country_not_supported'
          ) {
            modal('warning', {
              message: this.$t('signup.errors.country_not_supported.message'),
              subMessage: this.$t(
                'signup.errors.country_not_supported.subMessage',
                brandName(),
              ),
              learnMoreLink: helpCenterArticleUrl(572124),
            });
            this.isLoading = false;
            this.resetTurnstile();
            return;
          }

          if (signup.error) {
            logValidationError('signup', signup.error, signupAttributes);
          }

          sendNativeAppMessage({
            message: NativeAppMessage.UserLogin,
          });

          const { fetchAllData } = useSessionStore();
          fetchAllData().then(() => {
            const { locationId } = useLocationsStore();
            const { companyId } = useCompanyStore();

            this.$router.push({
              name: 'calendar',
              params: {
                companyId,
                locationId,
              },
              query: {
                welcome: true,
              },
            });

            const pageLayoutStore = usePageLayoutStore();
            pageLayoutStore.isNavExpanded = true;
          });

          if (this.$gtm) {
            this.$gtm.trackEvent({
              event: 'sign_up_complete',
            });
          }
        })
        .catch(() => {
          this.resetTurnstile();
          this.isLoading = false;
        });
    },
  },
  mounted() {
    const { startUnleash } = useSessionStore();

    $axios
      .get('/sessions/visitor_locale')
      .then(({ data: { country_code: countryCode } }) => {
        this.countryCode = countryCode;
      });

    startUnleash();
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  height: 100%;
}

.prefixBase {
  display: flex;
  width: 100%;
  align-items: start;
}

.prefixStyle {
  width: 10%;
  background-color: var(--color-bg-secondary);
  height: 42px;
  border: var(--color-border) 1px solid;
  border-radius: $radius;
  margin-bottom: $spacing;

  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: $spacing * 0.2;
  cursor: not-allowed;
}

.phone {
  width: 100%;
}

.nav {
  display: flex;
  justify-content: center;
  padding-top: $spacing * 2;
}

.treatwellLogo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  max-width: 260px;
  object-fit: contain;
  object-position: center;
  margin: 0 auto;
  margin-bottom: $spacing;
}

.turnstileError {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing;
}

.turnstilePlaceholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing * 0.5;
}
</style>
